import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { provjeriIzdanje } from '../pwa/izdanje'
import { dosegnutPrag, napunjenost, naVrhu, pomakPovlacenja } from './povlacenje'

/**
 * OSVJEŽAVANJE POVLAČENJEM — registar i gesta (krug 10, nalaz 4).
 *
 * Dvije stvari koje moraju stajati zajedno, jer bi razdvojene lagale jedna
 * drugoj:
 *
 * 1. **tko se osvježava** — ljuska ne zna što je na ekranu, a ekran ne zna za
 *    gestu. Zato svaki ekran (i sama ljuska) PRIJAVI svoj dohvat dok je montiran,
 *    a gesta pokrene sve prijavljeno. Popis se drži u `ref`u, ne u stanju: upis i
 *    ispis su posljedica montaže, a ne razlog za ponovno iscrtavanje.
 * 2. **provjera novog izdanja** — povlačenje je jedini trenutak u kojem korisnik
 *    izrijekom traži "provjeri ponovno", pa se uz podatke provjerava i build
 *    (`pwa/izdanje.ts`). Instalirana aplikacija vozača zna stajati otvorena cijelu
 *    smjenu i inače bi novi build dočekala tek na sljedećem mjeraču. Traka o novom
 *    izdanju se pritom NE prikazuje sama od sebe: provjera samo pita, a odgovor
 *    dolazi kroz `controllerchange`, kao i inače.
 *
 * **Osvježavanje NIKAD ne baca.** Pad dohvata je stvar ekrana koji ga je prijavio
 * (on već ima svoj prikaz greške); gesta koja zbog toga ostane zaglavljena u
 * vrtnji bila bi druga greška povrh prve.
 */

type Dohvat = () => Promise<unknown>

interface Registar {
  prijavi: (dohvat: Dohvat) => () => void
  osvjezi: () => Promise<void>
}

const Kontekst = createContext<Registar | null>(null)

export function PruzateljOsvjezavanja({ children }: { children: ReactNode }) {
  const dohvati = useRef(new Set<Dohvat>())

  const prijavi = useCallback((dohvat: Dohvat) => {
    dohvati.current.add(dohvat)

    return () => {
      dohvati.current.delete(dohvat)
    }
  }, [])

  const osvjezi = useCallback(async () => {
    // Snimka popisa: dohvat koji se usput odjavi (navigacija usred povlačenja)
    // ne smije promijeniti skup po kojem se upravo prolazi.
    const svi = [...dohvati.current].map((dohvat) => dohvat())

    await Promise.allSettled([...svi, provjeriIzdanje()])
  }, [])

  const vrijednost = useMemo<Registar>(() => ({ prijavi, osvjezi }), [prijavi, osvjezi])

  return <Kontekst.Provider value={vrijednost}>{children}</Kontekst.Provider>
}

/**
 * Ekran prijavljuje svoj dohvat dok stoji na ekranu.
 *
 * Funkcija se drži u `ref`u, pa inline strelica u komponenti ne odjavljuje i ne
 * prijavljuje registraciju pri svakom iscrtavanju (isti obrazac kao
 * `useResource`).
 */
export function useOsvjezavanje(dohvat: Dohvat): void {
  const registar = useContext(Kontekst)
  const zadnji = useRef(dohvat)
  zadnji.current = dohvat

  useEffect(() => {
    if (!registar) return

    return registar.prijavi(() => zadnji.current())
  }, [registar])
}

/**
 * Ljuska prijavljuje SVOJ dohvat (boravak, stanje smjene, otvoreni nalozi), a
 * hook mora vidjeti kontekst koji ista ljuska pruža — pa to radi dijete koje ne
 * crta ništa. Isti obrazac kao `PostaviZaglavlje` u ljusci gosta.
 */
export function PrijaviOsvjezavanje({ dohvat }: { dohvat: Dohvat }) {
  useOsvjezavanje(dohvat)

  return null
}

/**
 * Gesta nad platnom: povlačenje s vrha → indikator → osvježavanje.
 *
 * Slušači su `passive: false`, jer se povlačenje mora oteti pregledniku
 * (`preventDefault`) dok traje — inače bi ispod naše gume krenulo i elastično
 * odmicanje. Otima se SAMO dok se stvarno povlači prema dolje s vrha; svako drugo
 * listanje prolazi netaknuto.
 */
export function usePovlacenje(element: HTMLElement | null): {
  pomak: number
  osvjezava: boolean
} {
  const registar = useContext(Kontekst)
  const [pomak, postaviPomak] = useState(0)
  const [osvjezava, postaviOsvjezavanje] = useState(false)

  const osvjezi = registar?.osvjezi

  /*
    Element dolazi kao VRIJEDNOST, ne kao `ref`, i to je uvjet da gesta preživi
    navigaciju: ljuska pri prelasku na kartu zamijeni platno drugim elementom
    (kadar), a efekt vezan uz `ref` se zbog toga ne bi ponovno pokrenuo —
    slušači bi ostali na odspojenom čvoru, a novo platno bi bilo gluho. Zato ga
    pozivatelj drži u stanju i predaje ovamo; promjena elementa je onda promjena
    ovisnosti.
  */
  useEffect(() => {
    if (!element || !osvjezi) return

    // Lokalne konstante, pa ih rukovatelji ispod smiju čitati bez upitnika:
    // parametar hooka TypeScript unutar ugniježđene funkcije ne suzuje.
    const platno = element
    const pokreni = osvjezi

    // Ni jedno od ovoga nije stanje ekrana: unutar geste se mijenja pri svakom
    // pomaku prsta, a iscrtavanje traži samo pomak.
    let pocetak: number | null = null
    let povuceno = 0
    let uTijeku = false

    function naPocetak(dogadaj: TouchEvent) {
      // Dva prsta su zumiranje, ne povlačenje; osvježavanje u tijeku ne prima
      // drugu gestu.
      if (uTijeku || dogadaj.touches.length !== 1) return

      pocetak = naVrhu(platno.scrollTop) ? (dogadaj.touches[0]?.clientY ?? null) : null
      povuceno = 0
    }

    function naPomak(dogadaj: TouchEvent) {
      if (pocetak === null) return

      const y = dogadaj.touches[0]?.clientY ?? pocetak
      const razlika = y - pocetak

      // Prst je krenuo gore: ovo je listanje. Gesta se odustaje i više se ne
      // hvata do sljedećeg dodira — inače bi se popis usred listanja "zalijepio".
      if (razlika <= 0) {
        pocetak = null
        povuceno = 0
        postaviPomak(0)
        return
      }

      povuceno = pomakPovlacenja(razlika)
      postaviPomak(povuceno)

      if (dogadaj.cancelable) dogadaj.preventDefault()
    }

    function naKraj() {
      const okini = pocetak !== null && dosegnutPrag(povuceno)

      pocetak = null
      povuceno = 0

      if (!okini) {
        postaviPomak(0)
        return
      }

      uTijeku = true
      postaviOsvjezavanje(true)
      // Indikator ostaje na pragu dok posao traje: guma koja se vrati na nulu, a
      // spinner se vrti, izgleda kao da se gesta nije primila.
      postaviPomak(0)

      void pokreni().finally(() => {
        uTijeku = false
        postaviOsvjezavanje(false)
      })
    }

    platno.addEventListener('touchstart', naPocetak, { passive: true })
    platno.addEventListener('touchmove', naPomak, { passive: false })
    platno.addEventListener('touchend', naKraj)
    platno.addEventListener('touchcancel', naKraj)

    return () => {
      platno.removeEventListener('touchstart', naPocetak)
      platno.removeEventListener('touchmove', naPomak)
      platno.removeEventListener('touchend', naKraj)
      platno.removeEventListener('touchcancel', naKraj)
    }
  }, [element, osvjezi])

  return { pomak, osvjezava }
}

/**
 * Površina koja se povlači, a NIJE platno ljuske.
 *
 * Postoji zbog jednog ekrana i to je odluka, ne iznimka: karta JEST kadar, pa na
 * njoj prst pomiče kartu — gesta osvježavanja ondje bi gostu vukla svijet ispod
 * prsta. Ali list nad kartom (status naloga, procjena, put do praćenja) je
 * običan sadržaj i njega se povuče kao i svaki drugi. Tako karta dobiva
 * osvježavanje bez da izgubi pomicanje.
 */
export function PovuciZaOsvjezavanje({
  className,
  style,
  children,
}: {
  className?: string
  style?: React.CSSProperties
  children: ReactNode
}) {
  const [okvir, postaviOkvir] = useState<HTMLDivElement | null>(null)
  const { pomak, osvjezava } = usePovlacenje(okvir)

  return (
    <div
      ref={postaviOkvir}
      className={className}
      style={{
        ...style,
        transform: pomak > 0 ? `translateY(${Math.min(pomak, 36)}px)` : undefined,
        transition: pomak > 0 ? undefined : 'transform 180ms ease-out',
      }}
    >
      <IndikatorPovlacenja pomak={pomak} osvjezava={osvjezava} />
      {children}
    </div>
  )
}

/**
 * Zlatni indikator (§17: boje iz tokena, nijedna heksadekadska vrijednost).
 *
 * Prsten koji se puni dok se vuče i vrti dok se osvježava — jedan oblik za oba
 * stanja, jer je to jedna radnja u dvije faze. Ne nosi tekst: povlačenje je gesta
 * koja se ili razumije ili se ne dogodi, a natpis ispod prsta ionako pokriva prst.
 */
export function IndikatorPovlacenja({
  pomak,
  osvjezava,
}: {
  pomak: number
  osvjezava: boolean
}) {
  if (pomak <= 0 && !osvjezava) return null

  const udio = osvjezava ? 1 : napunjenost(pomak)
  const vrh = osvjezava ? 20 : Math.min(pomak, 72)

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-center"
      style={{ transform: `translateY(${vrh - 28}px)`, opacity: 0.35 + udio * 0.65 }}
    >
      <span
        className={`grid size-9 place-items-center rounded-full border border-brass bg-surface ${
          osvjezava ? 'animate-spin' : ''
        }`}
        style={osvjezava ? undefined : { transform: `rotate(${udio * 270}deg)` }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="text-champagne"
        >
          <path d="M20 12a8 8 0 1 1-2.3-5.6" />
          <path d="M20 4v4.5h-4.5" />
        </svg>
      </span>
    </div>
  )
}
